/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Drawer from '@mui/material/Drawer';
import { loadProjectionSystems } from '../../store/actions/projectionSystems';
import { MmgProjectCreatePanel } from '../../projects/project-create-panel';
import { MmgWorkspaceCreatePanel } from './workspace-create-panel';
import { MmgWorkspaceCreateButtons } from './workspace-create-buttons';
import { IProjection } from '../../models/IProjections';
import { IProject } from '../../models/IProject';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import WorkspaceManager from '../../managers/WorkspaceManager';
import { t } from '../../translations/i18n';
import { getRouteByPath, ROUTES } from '../../app/routes';
import { WorkspaceActionType } from '../store/WokspaceActionType';
import { useNavigate, useParams } from 'react-router-dom';
import { IGlobalState } from '../../store/reducers';
import { store } from '../../store';
import MikeButton from '../../shared-components/mike-button';
const WorkspaceCreateStyle = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  height: 100%;
`;

const WorkspaceCreateDrawerContentStyle = css`
  width: 100%;

  @media (min-width: 460px) {
    width: 400px;
  }
`;

/**
 * @name MmgConnectedWorkspaceCreate
 * @summary Allows creating a new workspace.
 *
 */
export const MmgConnectedWorkspaceCreate = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const project: IProject = useSelector((state: IGlobalState) => state.ProjectReducer.project);
  const projectSelectedForDelete: Array<string> = useSelector(
    (state: IGlobalState) => state.ProjectReducer.projectSelectedForDelete,
  );
  const loadingProjectionSystems: boolean = useSelector(
    (state: IGlobalState) => state.ProjectionsReducer.loadingProjectionSystems,
  );
  const searchProjectionSystemsById: boolean = useSelector(
    (state: IGlobalState) => state.ProjectionsReducer.searchProjectionSystemsById,
  );
  const selectedProjectionSystems: IProjection[] = useSelector(
    (state: IGlobalState) => state.ProjectionsReducer.selectedProjectionSystems,
  );

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId);
  const canCreateWorkspace = project && project.capabilities && project.capabilities.canUpdateContent;
  const canDeleteWorkspace = project && project.capabilities && project.capabilities.canDeleteContent;

  // on mount load Projections
  useEffect(() => {
    store.dispatch(loadProjectionSystems());
  }, []);

  /**
   * Toggles create workspace modal.
   */
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  /**
   * Callback for start creating a workspace in existing project
   */
  const onCreateInExistingProject = () => {
    setCreateProjectOpen(false);
    setDrawerOpen(true);
  };

  const cancelCreateWorkspace = () => {
    setDrawerOpen(false);
    setCreateProjectOpen(false);
    setSelectedProjectId(projectId);
  };
  /**
   * Callback for creating a new  project
   * @param newProject
   */
  const onProjectCreated = (newProject) => {
    setSelectedProjectId(newProject.id);
    setCreateProjectOpen(false);
  };

  const handleDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleDialogOpen = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteClick = () => {
    try {
      for (let i = 0; i < projectSelectedForDelete.length; i++) {
        WorkspaceManager.deleteWorkspace(projectSelectedForDelete[i])
          .then(() => {
            const toast = {
              text: t('DELETED', 1, { thing: projectSelectedForDelete[i] }),
            };

            store.dispatch({ type: 'workspace/recent/DELETE', workspaceId: projectSelectedForDelete[i] });
            store.dispatch({ type: 'toast/ADD/SUCCESS', toast });
            const route = getRouteByPath(ROUTES.workspaceList.path, { projectId });
            navigate(route);           
          })
          .catch(() => {})
          .finally(() => {
            store.dispatch({ type: WorkspaceActionType.WORKSPACE_LOAD_OVERVIEW, data: { projectId, navigate } });
            setDeleteDialogOpen(false);
          });
      }
    } catch (ex: any) {
      console.error('inner', ex.message);
    } 
  };

  return (
    <div css={WorkspaceCreateStyle}>
      <MmgWorkspaceCreateButtons
        canCreateWorkspace={canCreateWorkspace}
        onCreateInExistingProject={onCreateInExistingProject}
        canDeleteWorkspace={canDeleteWorkspace}
        handleDialogClose={handleDialogClose}
        handleDialogOpen={handleDialogOpen}
        projectSelectedForDelete={projectSelectedForDelete}
      />
      {deleteDialogOpen && (
        <Dialog onClose={handleDialogClose} aria-labelledby="simple-dialog-title" open={deleteDialogOpen}>
          <DialogTitle id="simple-dialog-title">Delete confirmation</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">Are you sure you want to delete?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MikeButton variant="outlined" onClick={handleDialogClose} color="primary">
              Cancel
            </MikeButton>
            <MikeButton variant="contained" color="secondary" onClick={handleDeleteClick} autoFocus>
              Delete
            </MikeButton>
          </DialogActions>
        </Dialog>
      )}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer}>
        <div css={WorkspaceCreateDrawerContentStyle}>
          <div hidden={createProjectOpen}>
            <MmgWorkspaceCreatePanel
              projectId={selectedProjectId}
              onPanelExit={cancelCreateWorkspace}
              loadingProjectionSystems={loadingProjectionSystems}
              searchProjectionSystemsById={searchProjectionSystemsById}
              selectedProjectionSystems={selectedProjectionSystems}
            />
          </div>

          {createProjectOpen && (
            <MmgProjectCreatePanel onProjectCreated={onProjectCreated} onPanelExit={toggleDrawer} />
          )}
        </div>
      </Drawer>
    </div>
  );
};
